import type {
    RegimeDependenceProfileComparison,
    RegimeDependenceProfileReport,
    RegimeDependenceProfileReportSummary,
    RegimeProfileComparisonPreconditions,
    RegimeProfileSliceMetrics
} from '@/types/appRouter';
import {
    formatInt,
    formatShare,
    getPrimaryDimension,
    getRelativeEdgeChipState,
    getRelativeEdgeDisqualificationLabel,
    type RelativeEdgeChipInput
} from './regimeProfilePresentation';

/**
 * Pure presentation logic for Layer 5 — the post-AERO comparison leg
 * (docs/plans/REGIME_DEPENDENCE_PROFILE_FRONTEND_PLAN.md, Surface 1, Layer 5).
 *
 * Two honest non-success shapes are first-class here, never errors: a comparison the backend REFUSED
 * (`status: 'refused'` with a stable code — the two artifacts may not be compared at all) and a comparison
 * that ran but withheld attribution (`diagnosis: 'combined_basis_difference'` — a precondition failed, so
 * differences are not attributable to the AERO exit alone). Unknown codes from a newer backend must still
 * render, naming the raw code — the Layer 1b checklist convention; nothing may silently vanish.
 */

// ---------------------------------------------------------------------------
// Leg availability
// ---------------------------------------------------------------------------

export type ComparisonAvailability =
    | { kind: 'ready'; postSummary: RegimeDependenceProfileReportSummary }
    /** No realized strategy-exit report exists for this experiment yet. */
    | { kind: 'missingPostLeg' }
    /** A realized report exists but no fixed-horizon report does, so there is no pre-AERO leg. */
    | { kind: 'missingPreLeg'; postSummary: RegimeDependenceProfileReportSummary };

/** Newest realized strategy-exit summary for the experiment (summaries arrive newest first). */
export function selectPostAeroLegSummary(
    summaries: readonly RegimeDependenceProfileReportSummary[],
    experimentId: number
): RegimeDependenceProfileReportSummary | null {
    return summaries.find((summary) => summary.experimentId === experimentId && summary.outcomeMode === 'realizedStrategyExit') ?? null;
}

/**
 * Self-sufficient over the experiment's verdict history: the pre-leg presence is derived from the same
 * summaries rather than trusted from the caller, so it cannot disagree with the list it was given. This
 * matches the panel's main-leg selection, which shows a fixed-horizon report exactly when the history
 * contains one.
 */
export function getComparisonAvailability(params: {
    summaries: readonly RegimeDependenceProfileReportSummary[];
    experimentId: number;
}): ComparisonAvailability {
    const postSummary = selectPostAeroLegSummary(params.summaries, params.experimentId);

    if (postSummary === null) return { kind: 'missingPostLeg' };

    const hasFixedHorizonLeg = params.summaries.some(
        (summary) => summary.experimentId === params.experimentId && summary.outcomeMode === 'fixedHorizon'
    );

    if (!hasFixedHorizonLeg) return { kind: 'missingPreLeg', postSummary };

    return { kind: 'ready', postSummary };
}

// ---------------------------------------------------------------------------
// Diagnosis presentation
// ---------------------------------------------------------------------------

export type ComparisonDiagnosisTone = 'success' | 'warning' | 'neutral';

export interface ComparisonDiagnosisPresentation {
    label: string;
    tone: ComparisonDiagnosisTone;
    /** One-sentence plain-English meaning. Only entry_exit_interaction attributes anything to the exit. */
    gloss: string;
    /** What would make the comparison attributable or conclusive; null when nothing is withheld. */
    unblockHint: string | null;
    /** The diagnosis code was not recognized by this build (newer backend); rendered raw, never hidden. */
    unknownDiagnosis: boolean;
}

/**
 * What would unblock exit-attribution for a combined_basis_difference: named per failed precondition,
 * from the comparison's own recorded evidence (never hard-coded shares).
 *
 * The option-instrument case must not promise an impossible path: an option-basis fixed-horizon report
 * carries a newer report version than realized reports do, and the comparator hard-refuses any version
 * difference (report_version_mismatch) — "run an option-basis pre-leg" would only trade this diagnosis
 * for an unsatisfiable refusal, since report versions are policy constants, not caller choices. The
 * honest hint is that attribution is structurally unavailable until option-basis report-version parity
 * lands.
 */
function buildCombinedBasisUnblockHint(preconditions: RegimeProfileComparisonPreconditions): string {
    const populationNeed = `legs sharing at least ${formatShare(preconditions.matchedPopulation.minSharedShare)} of each other's profiled entries`;

    if (!preconditions.instrumentBasis.passed && preconditions.instrumentBasis.postAeroRealizedInstrumentBasis === 'option') {
        return (
            'Exit-effect attribution is structurally unavailable for this pairing today: the realized leg traded options, ' +
            'an option-basis pre-AERO profile exists only at a newer report version than realized reports carry, and the ' +
            'comparator requires equal report versions. Attribution becomes possible once option-basis report-version ' +
            'parity lands with the option-baseline lifecycle parity work.' +
            (preconditions.matchedPopulation.passed
                ? ''
                : ` The legs would also need to share at least ${formatShare(preconditions.matchedPopulation.minSharedShare)} of each other's profiled entries.`)
        );
    }

    const needs: string[] = [];

    if (!preconditions.instrumentBasis.passed) {
        const realizedBasis = preconditions.instrumentBasis.postAeroRealizedInstrumentBasis;

        needs.push(
            realizedBasis === 'mixed'
                ? 'a realized outcome set that trades a single instrument'
                : `a pre-AERO fixed-horizon profile on the ${realizedBasis} basis`
        );
    }

    if (!preconditions.matchedPopulation.passed) {
        needs.push(populationNeed);
    }

    if (needs.length === 0) {
        // Defensive: this diagnosis only arises from a failed precondition, but never render a blank hint.
        return 'To attribute differences to the AERO exit alone, both comparison preconditions must pass.';
    }

    return `To attribute differences to the AERO exit alone, this comparison needs ${needs.join(' and ')}.`;
}

export function getComparisonDiagnosisPresentation(comparison: {
    /** Widened to string: the runtime value may come from a newer backend than this build's contract. */
    diagnosis: string;
    preconditions: RegimeProfileComparisonPreconditions;
}): ComparisonDiagnosisPresentation {
    const { diagnosis } = comparison;

    switch (diagnosis) {
        case 'aligned':
            return {
                gloss: 'The verdict, adverse states, and veto states are unchanged between the pre-AERO and post-AERO legs.',
                label: 'Aligned',
                tone: 'success',
                unblockHint: null,
                unknownDiagnosis: false
            };
        case 'entry_exit_interaction':
            return {
                gloss: 'The AERO exit changed the regime picture. Both preconditions passed, so the differences below are attributable to the exit.',
                label: 'Entry/exit interaction',
                tone: 'warning',
                unblockHint: null,
                unknownDiagnosis: false
            };
        case 'combined_basis_difference':
            return {
                gloss:
                    'The two legs differ, but a comparison precondition failed — the differences reflect combined ' +
                    'instrument/execution/exit sensitivity and are not attributable to the AERO exit alone.',
                label: 'Combined basis difference',
                tone: 'neutral',
                unblockHint: buildCombinedBasisUnblockHint(comparison.preconditions),
                unknownDiagnosis: false
            };
        case 'insufficient_comparison':
            return {
                gloss: 'At least one leg reached insufficient evidence, so the two legs cannot be compared reliably.',
                label: 'Insufficient comparison',
                tone: 'neutral',
                unblockHint: 'Continue other treatments; revisit this optional comparison only if more evidence would help the decision.',
                unknownDiagnosis: false
            };
        default:
            return {
                gloss: `The comparator returned diagnosis "${diagnosis}", which this build does not recognize. Treat the comparison as diagnostic only.`,
                label: diagnosis,
                tone: 'neutral',
                unblockHint: null,
                unknownDiagnosis: true
            };
    }
}

// ---------------------------------------------------------------------------
// Refusal presentation — a refused comparison is a first-class honest state, not an error
// ---------------------------------------------------------------------------

export interface ComparisonRefusalPresentation {
    /** Short human title for why the comparison is withheld. */
    title: string;
    /** Plain-English explanation of the incompatibility. */
    explanation: string;
    /** What would unblock the comparison. */
    unblockHint: string;
    /** The refusal code was not recognized by this build (newer backend); rendered raw, never hidden. */
    unknownCode: boolean;
}

export function getComparisonRefusalPresentation(code: string, mismatchedFields: readonly string[]): ComparisonRefusalPresentation {
    switch (code) {
        case 'pre_leg_outcome_mode_invalid':
            return {
                explanation: 'The pre-AERO leg must profile fixed-horizon outcomes; the selected report profiles realized outcomes.',
                title: 'Pre-AERO leg has the wrong outcome mode',
                unblockHint: 'Run a fixed-horizon profile for this experiment to serve as the pre-AERO leg.',
                unknownCode: false
            };
        case 'post_leg_outcome_mode_invalid':
            return {
                explanation:
                    'The post-AERO leg must profile realized strategy-exit outcomes; the selected report profiles fixed-horizon outcomes.',
                title: 'Post-AERO leg has the wrong outcome mode',
                unblockHint: "Run a realized strategy-exit profile over the AERO finalist's outcome set to serve as the post-AERO leg.",
                unknownCode: false
            };
        case 'relative_edge_evidence_unresolved':
            return {
                explanation:
                    'One leg does not carry machine-resolved relative-edge evidence (it predates report version 4), so the ' +
                    'comparator cannot verify the edge policy on both sides.',
                title: 'A leg lacks resolved relative-edge evidence',
                unblockHint: "Re-run that leg's profile — report version 4 and later resolve the evidence automatically.",
                unknownCode: false
            };
        case 'included_entry_ids_missing':
            return {
                explanation:
                    'One leg does not record which entries it profiled (it predates report version 4), so the matched-population ' +
                    'precondition cannot be checked.',
                title: 'A leg lacks its profiled entry ids',
                unblockHint: "Re-run that leg's profile under report version 4 or later.",
                unknownCode: false
            };
        case 'report_version_mismatch':
            return {
                explanation:
                    'Report versions differ. Revisions can change outcome-completion lineage, option attribution, ' +
                    'cross-sectional coverage, or relative-edge policy; comparing unlike revisions would mix evidence definitions.',
                title: 'Report versions differ',
                unblockHint: 'Re-run both legs under the same report version.',
                unknownCode: false
            };
        case 'lineage_mismatch':
            return {
                explanation:
                    'The two reports resolve different lineage' +
                    (mismatchedFields.length > 0 ? ` (${mismatchedFields.join(', ')})` : '') +
                    ' and may not be compared: two profiles can legitimately disagree when they resolved different classifiers or windows.',
                title: 'The legs resolve different lineage',
                unblockHint: 'Re-run the stale leg so both reports resolve the same experiment window and model versions.',
                unknownCode: false
            };
        case 'pre_outcome_basis_missing':
            return {
                explanation:
                    'The pre-AERO report does not record which outcome basis it profiled, so the instrument-basis precondition ' +
                    'cannot be checked.',
                title: 'Pre-AERO leg is missing its outcome basis',
                unblockHint: 'Re-run the pre-AERO fixed-horizon profile.',
                unknownCode: false
            };
        case 'post_realized_instrument_basis_missing':
            return {
                explanation:
                    'The post-AERO realized report does not record which instruments its trades executed, so the instrument-basis ' +
                    'precondition cannot be checked.',
                title: 'Post-AERO leg is missing its instrument mix',
                unblockHint: 'Re-run the post-AERO realized profile under report version 4 or later.',
                unknownCode: false
            };
        default:
            // A code this build does not know (newer backend) must never vanish — name it raw.
            return {
                explanation:
                    `The backend refused this comparison with code "${code}", which this build does not recognize.` +
                    (mismatchedFields.length > 0 ? ` Mismatched fields: ${mismatchedFields.join(', ')}.` : ''),
                title: 'Comparison withheld',
                unblockHint: "The refusal message below carries the backend's own explanation.",
                unknownCode: true
            };
    }
}

// ---------------------------------------------------------------------------
// Precondition rows — rendered as visible pass/fail rows ABOVE the diff
// ---------------------------------------------------------------------------

export interface ComparisonPreconditionRow {
    key: 'instrumentBasis' | 'matchedPopulation';
    label: string;
    passed: boolean;
    detail: string;
}

export function buildComparisonPreconditionRows(preconditions: RegimeProfileComparisonPreconditions): ComparisonPreconditionRow[] {
    const instrument = preconditions.instrumentBasis;
    const population = preconditions.matchedPopulation;
    const { overlap } = population;

    return [
        {
            detail: `pre ${instrument.preAeroOutcomeBasis} vs post realized ${instrument.postAeroRealizedInstrumentBasis}`,
            key: 'instrumentBasis',
            label: 'Instrument basis',
            passed: instrument.passed
        },
        {
            detail:
                `${formatInt(overlap.sharedCount)} shared entries — ${formatShare(overlap.sharedShareOfPre, 1)} of pre / ` +
                `${formatShare(overlap.sharedShareOfPost, 1)} of post (need ≥ ${formatShare(population.minSharedShare)} both ways)`,
            key: 'matchedPopulation',
            label: 'Matched population',
            passed: population.passed
        }
    ];
}

// ---------------------------------------------------------------------------
// Per-state deltas — direction must be obvious (a sign flip is the headline finding)
// ---------------------------------------------------------------------------

export interface ComparisonDeltaLeg {
    meanProfitPercent: number;
    tradeCount: number;
    episodeCount: number;
    /** tradeCount below that leg's own minTradesPerSlice: greyed, never dropped. */
    belowEvaluationMinimum: boolean;
}

export interface ComparisonStateDeltaRow {
    state: string;
    pre: ComparisonDeltaLeg | null;
    post: ComparisonDeltaLeg | null;
    /** post − pre winsorized mean; null unless the state appears in both legs. */
    deltaMeanProfitPercent: number | null;
    /**
     * Strict sign flip of the winsorized mean between the legs. Deliberately strict — no epsilon band —
     * because the evaluability gate already mutes the pathological small-sample cases: an unevaluable
     * row's flip is replaced by a below-minimum note and its delta is greyed, so a hair's-width flip can
     * only headline on slices both legs deem evaluable.
     */
    signFlip: 'negativeToPositive' | 'positiveToNegative' | null;
    /**
     * True only when BOTH legs are present and each meets its own report's minTradesPerSlice. An
     * unevaluable row keeps its delta for transparency, but it renders greyed, sorts below every
     * evaluable row, and its direction note is replaced by a below-minimum note.
     */
    evaluable: boolean;
    /** State named adverse by that leg's verdict reasons (from the comparison's recorded lists). */
    adversePre: boolean;
    adversePost: boolean;
}

function buildDeltaLeg(slice: RegimeProfileSliceMetrics, minTradesPerSlice: number): ComparisonDeltaLeg {
    return {
        belowEvaluationMinimum: slice.tradeCount < minTradesPerSlice,
        episodeCount: slice.episodeCount,
        meanProfitPercent: slice.meanProfitPercent,
        tradeCount: slice.tradeCount
    };
}

/** Sort tier: evaluable rows first, then unevaluable both-leg rows, then states missing a leg. */
function deltaRowSortRank(row: ComparisonStateDeltaRow): number {
    if (row.pre === null || row.post === null) return 2;

    return row.evaluable ? 0 : 1;
}

/**
 * One row per state in the union of the two legs' primary panels, in three tiers: evaluable rows first
 * (both legs meet their own minTradesPerSlice), sorted by mean-profit delta ascending so the worst
 * deterioration surfaces at the top edge, mirroring the primary panel's adverse-first sort; then
 * unevaluable both-leg rows, also delta-ascending, so noise never headlines above evidence; then states
 * missing a leg, by name. Each leg is judged against its own report's echoed minTradesPerSlice — two
 * reports may legitimately carry different constants. No row is ever dropped.
 */
export function buildComparisonStateDeltaRows(params: {
    preReport: Pick<RegimeDependenceProfileReport, 'dimensions' | 'thresholds'>;
    postReport: Pick<RegimeDependenceProfileReport, 'dimensions' | 'thresholds'>;
    comparison: Pick<RegimeDependenceProfileComparison, 'preAeroAdverseStates' | 'postAeroAdverseStates'>;
}): ComparisonStateDeltaRow[] {
    const prePrimary = getPrimaryDimension(params.preReport);
    const postPrimary = getPrimaryDimension(params.postReport);
    const preByState = new Map(prePrimary.slices.map((slice) => [slice.state, slice]));
    const postByState = new Map(postPrimary.slices.map((slice) => [slice.state, slice]));
    const adversePre = new Set(params.comparison.preAeroAdverseStates);
    const adversePost = new Set(params.comparison.postAeroAdverseStates);

    const states = [...new Set([...preByState.keys(), ...postByState.keys()])];

    const rows = states.map((state): ComparisonStateDeltaRow => {
        const preSlice = preByState.get(state);
        const postSlice = postByState.get(state);
        const pre = preSlice !== undefined ? buildDeltaLeg(preSlice, params.preReport.thresholds.minTradesPerSlice) : null;
        const post = postSlice !== undefined ? buildDeltaLeg(postSlice, params.postReport.thresholds.minTradesPerSlice) : null;

        let deltaMeanProfitPercent: number | null = null;
        let signFlip: ComparisonStateDeltaRow['signFlip'] = null;

        if (pre !== null && post !== null) {
            deltaMeanProfitPercent = post.meanProfitPercent - pre.meanProfitPercent;

            if (pre.meanProfitPercent < 0 && post.meanProfitPercent > 0) signFlip = 'negativeToPositive';
            else if (pre.meanProfitPercent > 0 && post.meanProfitPercent < 0) signFlip = 'positiveToNegative';
        }

        return {
            adversePost: adversePost.has(state),
            adversePre: adversePre.has(state),
            deltaMeanProfitPercent,
            evaluable: pre !== null && post !== null && !pre.belowEvaluationMinimum && !post.belowEvaluationMinimum,
            post,
            pre,
            signFlip,
            state
        };
    });

    return rows.sort((a, b) => {
        const rankDifference = deltaRowSortRank(a) - deltaRowSortRank(b);

        if (rankDifference !== 0) return rankDifference;
        if (a.deltaMeanProfitPercent === null && b.deltaMeanProfitPercent === null) return a.state.localeCompare(b.state);
        if (a.deltaMeanProfitPercent === null) return 1;
        if (b.deltaMeanProfitPercent === null) return -1;

        return a.deltaMeanProfitPercent - b.deltaMeanProfitPercent || a.state.localeCompare(b.state);
    });
}

export interface ComparisonDeltaRowNote {
    text: string;
    tone: 'positive' | 'negative' | 'neutral';
}

/**
 * Direction note for one delta row. An unevaluable row never gets a direction note — evaluability wins
 * over every other signal, so a below-minimum slice's flip cannot masquerade as a finding. For evaluable
 * rows, sign flips are the headline finding this leg exists to surface — e.g. recovery flipping from
 * adverse under the fixed-horizon entry to positive under the AERO exit — so a flip that corroborates
 * the adverse lists is worded in adverse terms, a bare sign flip in sign terms, and adverse-list
 * membership alone still gets a quiet note.
 */
export function getComparisonDeltaRowNote(row: ComparisonStateDeltaRow): ComparisonDeltaRowNote | null {
    if (row.pre !== null && row.post !== null && !row.evaluable) {
        const where =
            row.pre.belowEvaluationMinimum && row.post.belowEvaluationMinimum
                ? 'both legs'
                : row.pre.belowEvaluationMinimum
                  ? 'the pre-AERO leg'
                  : 'the post-AERO leg';

        return { text: `below evaluation minimum in ${where}`, tone: 'neutral' };
    }

    if (row.adversePre && row.adversePost) return { text: 'adverse in both legs', tone: 'negative' };
    if (row.adversePre && row.signFlip === 'negativeToPositive') return { text: 'flipped adverse → positive', tone: 'positive' };
    if (row.adversePost && row.signFlip === 'positiveToNegative') return { text: 'flipped positive → adverse', tone: 'negative' };
    if (row.signFlip === 'negativeToPositive') return { text: 'flipped negative → positive', tone: 'positive' };
    if (row.signFlip === 'positiveToNegative') return { text: 'flipped positive → negative', tone: 'negative' };
    if (row.adversePost) return { text: 'adverse post-AERO', tone: 'negative' };
    if (row.adversePre) return { text: 'adverse pre-AERO only', tone: 'neutral' };

    return null;
}

// ---------------------------------------------------------------------------
// Post-leg veto authorization — adverse states are not actionable without edge evidence
// ---------------------------------------------------------------------------

/**
 * Explain missing or weak optional baseline evidence without blocking research treatments.
 */
export function buildPostLegBenchmarkNote(postReport: RelativeEdgeChipInput): string | null {
    const chip = getRelativeEdgeChipState(postReport);

    if (chip.kind === 'resolved' && chip.qualifies) return null;

    const evidence = postReport.lineage.relativeEdgeEvidence;

    if (chip.kind === 'unresolved') {
        if (chip.disqualification === 'mixed_instrument_basis') {
            return (
                'The optional relative-edge benchmark is unresolved and the realized population ' +
                'mixes option and underlying instruments.'
            );
        }

        const basisNoun = evidence.instrumentBasis === 'mixed' ? 'matching' : evidence.instrumentBasis;

        return (
            `The optional relative-edge benchmark is unresolved: random-entry ${basisNoun} ` +
            `baselines cover ${formatInt(evidence.coveredCount)} of ${formatInt(evidence.eligibleCount)} eligible trades.`
        );
    }

    return (
        'The optional relative-edge benchmark does not qualify ' +
        `(${chip.disqualification !== null ? getRelativeEdgeDisqualificationLabel(chip.disqualification) : 'does not qualify'}).`
    );
}
